// "Try the full plan" sandbox API service.
//
// startSandbox() creates a fresh, private, seeded Heirloom demo event and
// returns couple tokens. The caller hands the built User + tokens to
// AuthProvider.enterSandbox(), which installs them in the per-tab sessionStorage
// scope (the real localStorage session is never touched).

import { postWithAuth, parseApiError } from 'utils/api-client';
import { adaptCoupleToUser } from './adapters';
import type { LoginResponse } from './auth';
import type { User } from 'types/users';

export interface SandboxStartResponse {
  success: boolean;
  token: string;
  refreshToken: string;
  couple: {
    id: string;
    email: string;
    eventId: string;
    displayName?: string;
    isEmailVerified: boolean;
  };
  event: {
    eventId: string;
    name: string;
    dateISO: string;
    eventType: string;
    plan: 'pro';
    isSandbox: true;
    sandboxExpiresAt: string;
  };
}

/**
 * Start a sandbox. `skipAuth` so any real token in the current tab is NOT
 * attached — the endpoint is public and serves logged-in and anonymous alike.
 *
 * `turnstileToken` carries the invisible Cloudflare Turnstile challenge result
 * (the BE `requireTurnstile()` gate on this endpoint requires it when
 * `TURNSTILE_SECRET_KEY` is set). Omitted when the site key is unconfigured —
 * the BE middleware is a no-op in that case, so it fails open for local dev.
 */
export async function startSandbox(
  lang?: string,
  turnstileToken?: string,
): Promise<SandboxStartResponse> {
  const res = await postWithAuth(
    '/api/sandbox/start',
    { lang, ...(turnstileToken ? { turnstileToken } : {}) },
    // skipAuth: the endpoint is public — never attach a real token from this tab.
    // skipRetry: a 429 (this IP hit the demo-start rate limit) must surface
    // immediately as a friendly error. Without it the shared api-client retries
    // 429 up to 10× with exponential backoff (~minutes), hanging the /try loader.
    { skipAuth: true, skipRetry: true },
  );
  if (!res.ok) throw await parseApiError(res, 'Could not start the demo');
  return res.json();
}

/**
 * Build the demo `User` for enterSandbox() from a start response. Reuses the
 * couple adapter, then stamps the sandbox marker + pro plan so gated UI unlocks
 * immediately (a subsequent /couple/me refresh fills storage/eventType).
 */
export function buildSandboxUser(resp: SandboxStartResponse): User {
  const base = adaptCoupleToUser(resp as unknown as LoginResponse);
  return {
    ...base,
    isSandbox: true,
    plan: 'pro',
    eventType: (resp.event.eventType as User['eventType']) ?? 'wedding',
    weddingDate: resp.event.dateISO,
  };
}

/** Wipe the current sandbox's data and re-seed fresh sample content in place. */
export async function resetSandbox(): Promise<void> {
  const res = await postWithAuth('/api/sandbox/reset', {});
  if (!res.ok) throw await parseApiError(res, 'Could not reset the demo');
}

/** Best-effort immediate teardown of the current sandbox (explicit exit). */
export async function endSandbox(): Promise<void> {
  try {
    await postWithAuth('/api/sandbox/end', {});
  } catch {
    // Best-effort — the hourly sweep reaps anything left behind.
  }
}
